import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Sparkles } from 'lucide-react';
import { motion, useAnimation } from 'framer-motion';
import ShaderBackground from './ShaderBackground';
import './Hero.css';

const Hero = () => {
  const [particles, setParticles] = useState([]);
  const logoControls = useAnimation();

  // Generate gold particles
  useEffect(() => {
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 10 + 15,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);

    // Logo light sweep animation
    logoControls.start({
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: 2,
        ease: "easeInOut"
      }
    });
  }, [logoControls]);

  // Split text animation for headline
  const text = "PSD Designers";
  const words = text.split(" ");

  return (
    <section className="hero-section">
      <ShaderBackground />
      
      {/* Gold Particle Drift */}
      <div className="gold-particles">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="gold-particle"
            initial={{ 
              x: `${particle.x}vw`, 
              y: `${particle.y}vh`,
              opacity: 0 
            }}
            animate={{
              y: [`${particle.y}vh`, `${particle.y - 30}vh`, `${particle.y}vh`],
              opacity: [0, 0.6, 0]
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`
            }}
          />
        ))}
      </div>

      <div className="hero-overlay"></div>
      
      <div className="hero-content">
        {/* Slow Parallax Floating Logo */}
        <motion.div
          className="hero-logo-container"
          initial={{ opacity: 0, y: 50 }}
          animate={{ 
            opacity: 1, 
            y: [0, -15, 0]
          }}
          transition={{
            opacity: { duration: 1 },
            y: {
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }
          }}
        >
          {/* Light Sweep on Logo */}
          <motion.div
            className="logo-wrapper"
            animate={logoControls}
          >
          
            <motion.div
              className="light-sweep"
              initial={{ x: "-100%" }}
              animate={{ x: "200%" }}
              transition={{
                duration: 2,
                ease: "easeInOut",
                delay: 0.5
              }}
            />
          </motion.div>
        </motion.div>

        {/* Text Reveal Split Animation - Center-split reveal */}
        <div className="hero-headline-wrapper">
          {words.map((word, wordIndex) => (
            <div key={wordIndex} className="word-container">
              {word.split('').map((char, charIndex) => {
                const totalChars = word.length;
                const middle = Math.floor(totalChars / 2);
                const distance = Math.abs(charIndex - middle);
                const isLeft = charIndex < middle;
                
                return (
                  <motion.span
                    key={charIndex}
                    className="hero-headline-char"
                    initial={{ 
                      opacity: 0,
                      x: isLeft ? -50 * (distance + 1) : 50 * (distance + 1)
                    }}
                    animate={{ 
                      opacity: 1,
                      x: 0
                    }}
                    transition={{
                      duration: 0.8,
                      delay: 1.2 + (distance * 0.05),
                      ease: "easeOut"
                    }}
                  >
                    {char}
                  </motion.span>
                );
              })}
            </div>
          ))}
        </div>
        
        {/* Text Glint Animation */}
        <motion.p 
          className="hero-tagline-main"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2 }}
        >
          <motion.span
            className="glint-text"
            animate={{
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            Where Stitch Meets Style
          </motion.span>
        </motion.p>
        
        <motion.h2 
          className="hero-subheadline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.3 }}
        >
          Bespoke Tailoring & Couture Excellence
        </motion.h2>
        
        <motion.p 
          className="hero-tagline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 2.6 }}
        >
          Handcrafted garments that embody elegance, precision, and timeless style. 
          Experience the art of custom tailoring at its finest.
        </motion.p>
        
        {/* Smooth Fade and Scale-In for CTA Buttons */}
        <motion.div 
          className="hero-cta-group"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 2.9 }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: 3,
              type: "spring",
              bounce: 0.4
            }}
          >
            <Link to="/contact" className="hero-cta-primary">
              <Calendar size={20} />
              <span>Book a Fitting</span>
              {/* Button Ripple Effect handled by CSS */}
              <span className="ripple"></span>
            </Link>
          </motion.div>
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: 3.2,
              type: "spring",
              bounce: 0.4
            }}
          >
            <Link to="/gallery" className="hero-cta-secondary">
              <Sparkles size={20} />
              <span>View Collection</span>
            </Link>
          </motion.div>
        </motion.div>
        
        <motion.p 
          className="hero-lead-magnet"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 3.4 }}
        >
          Transform your wardrobe with made-to-measure perfection
        </motion.p>
      </div>
    </section>
  );
};

export default Hero;